<template>
  <div v-if="record" class="order-detail">

    <section class="section">

      <div v-if="record.deleted_at" class="notification is-danger">
        This order has been deleted.
      </div>

      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <router-link to="/support-tickets" class="navbar-item">
              <span class="mdi mdi-chevron-left"></span>&nbsp; All Support Tickets
            </router-link>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <div class="field has-addons">
              <div class="control">
                <button class="button" @click="editRecord()">
                  <span class="icon icon-medium"><i class="mdi mdi-file-document-edit-outline mdi-18px"></i></span>
                  <span>Edit</span>
                </button>
              </div>
              <div v-if="is_admin && !record.transfer" class="control">
                <button v-if="record.deleted_at" class="button" @click="recoverRecord">
                  <span class="icon icon-medium"><i class="mdi mdi-delete-sweep-outline mdi-18px"></i></span>
                  <span>Recover</span>
                </button>
                <button v-else class="button" @click="deleteRecord">
                  <span class="icon icon-medium"><i class="mdi mdi-delete-forever-outline mdi-18px"></i></span>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="box is-relative">
        <order-status :record="record" />
        <div class="columns">

          <div class="column is-narrow">
            <p class="heading">Order Number</p>
            <h4 class="title is-4">{{ record.reference }}</h4>
          </div>
          <template v-if="record.data">
            <div v-if="record.data.release_number" class="column is-narrow">
              <p class="heading">Release Number</p>
              <h5 class="title is-4" data-attribute="data.order-number">{{ record.data.release_number }}</h5>
            </div>
            <div class="column is-narrow">
              <p class="heading">Sales Rep</p>
              <h5 class="title is-4" data-attribute="data.sales-rep">
                <span v-if="record.data.sales_rep">{{ record.data.sales_rep }}</span>
                <span v-else>Not Assigned</span>
              </h5>
            </div>
          </template>
        </div>
        <div class="b-table">
          <div class="table-wrapper">
            <table class="table is-striped is-narrow has-mobile-cards is-inbox">
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                   Priority:
                </th>
                <td data-label="Priority:">
                  <span :class="record.priority" class="tag priority" data-attribute="priority">
                    {{ record.priority | titleize }}
                  </span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                   Service Type:
                </th>
                <td data-label="Service Type:">
                  {{ record.data.service_type }}
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                   Support Assignment:
                </th>
                <td data-label="Support Assignment:">
                  {{ record.data.support_assignment }}
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Unit Count:
                </th>
                <td data-label="Unit Count:">
                  <span data-attribute="unit-count">{{ record.unit_count }}</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Created Date:
                </th>
                <td data-label="Created:">
                  <span v-if="record.created_at" data-attribute="created-at">{{ record.created_at | formattedDate }}</span>
                  <span v-else>N/A</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Customer Due Date:
                </th>
                <td data-label="Due:">
                  <span v-if="record.expected_at" data-attribute="expected-at">{{ record.expected_at | formattedDate }}</span>
                  <span v-else>N/A</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Estimated Completion Date:
                </th>
                <td data-label="Completion:">
                  <span v-if="record.expires_at" data-attribute="expires-at">{{ record.expires_at | formattedDate }}</span>
                  <span v-else>N/A</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Approval Date:
                </th>
                <td data-label="Approval Date:">
                  <span v-if="record.approved_at" data-attribute="expires-at">{{ record.approved_at | formattedDate }}</span>
                  <span v-else>N/A</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Completed Date:
                </th>
                <td data-label="Delivery Date:">
                  <span v-if="record.delivered_at" data-attribute="expires-at">{{ record.delivered_at | formattedDate }}</span>
                  <span v-else>N/A</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Client Name:
                </th>
                <td data-label="Name:">
                  <span data-attribute="data.client-name">{{ record.data.client_name }}</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Client Phone:
                </th>
                <td data-label="Phone:">
                  <span data-attribute="data.client-phone">{{ record.data.client_phone | phoneNumber }}</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Client Email:
                </th>
                <td data-label="Email:">
                  <span data-attribute="data.client-email">
                    <a v-if="record.data.client_email" :href="`mailto:${record.data.client_email}`">{{ record.data.client_email }}</a>
                  </span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Delivery City:
                </th>
                <td data-label="Delivery City:">
                  <span data-attribute="data.delivery-city">{{ record.data.delivery_city }}</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 240px;">
                  Site Ready:
                </th>
                <td data-label="Site Ready:">
                  <span v-show="record.data.site_ready !== undefined" data-attribute="data.site-ready">{{ record.data.site_ready | yesOrNo }}</span>
                </td>
              </tr>
              <tr>
                <th class="is-hidden-mobile" style="width: 150px;">
                  Created By:
                </th>
                <td data-label="Expired:">
                  <span v-if="record.created_by_user" data-attribute="created-by-user">
                    {{ record.created_by_user.first_name }}
                    {{ record.created_by_user.last_name }}
                  </span>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <div class="box">
        <h6 class="title is-6">Notes</h6>
        <div class="pre-wrapper">
          <pre data-attribute="notes">{{ record.notes }}</pre>
        </div>
      </div>

      <div class="box">
        <template v-if="pdfs.length">
          <h6 class="title is-6">PDFs</h6>
          <div v-for="url in pdfs" :key="url" class="mb-2">
            <h6 class="title is-6">
              <a :href="url" target="_blank">
                {{ url | basename }}
              </a>
            </h6>
            <embed :src="url" type="application/pdf" width="100%" style="height: 600px; background-color: #efefef;" />
            <a :href="url" target="_blank" class="is-size-6">
              Download
            </a>
          </div>
          <hr>
        </template>


        <template v-if="images.length">
          <h6 class="title is-6">Images</h6>
          <div class="columns">
            <div v-for="url in images" :key="url" class="column">
              <a :href="url" target="_blank" class="is-size-7">
                <img :src="url" width="400" />
                <br>
                {{ url | basename }}
              </a>
              <br>
              <a @click="deleteUpload(record, url)" class="is-danger">&times; delete</a>
            </div>
          </div>
        </template>

        <h6 class="title is-6">All Uploads</h6>
        <p v-for="url in uploads" :key="url">
          <a :href="url" target="_blank" class="is-size-7">
            {{ url | basename }}
          </a>
          <a @click="deleteUpload(record, url)" class="is-danger">&times;</a>
        </p>
      </div>

    </section>

  </div>

  <loader v-else />

</template>

<script>
  import filters from 'shared/filters'

  import UserHelper from 'shared/mixins/user-helper'
  import AsyncRecord from 'shared/mixins/async-record'

  import Loader from 'shared/components/loader'
  import OrderStatus from './status'
  import OrderForm from './form'

  export default {
    filters,
    mixins: [
      UserHelper,
      AsyncRecord
    ],
    components: {
      Loader,
      OrderStatus
    },
    data () {
      return {
        asyncRecordOptions: {
          type: 'order',
          options: {
            order_type: 'support_ticket',
            include: 'created_by_user'
          }
        }
      }
    },
    computed: {
      uploads () {
        return [ ...new Set(this.record.data.uploads || []) ]
      },
      pdfs () {
        return this.uploads.filter(i => i.endsWith('.pdf'))
      },
      images () {
        return this.uploads.filter(i => i.match(/(jpe?g|png|webp)$/))
      }
    },
    methods: {
      editRecord () {
        this.$buefy.modal.open({
          parent: this,
          component: OrderForm,
          props: {
            record: { ...this.record },
            onSave: record => {
              this.fetchRecord()
            }
          }
        })
      },
      deleteRecord () {
        this.$buefy.dialog.confirm({
          title: 'Deleting Support Ticket',
          message: 'Are you sure you want to <b>delete</b> this support ticket?',
          confirmText: 'Delete Support Ticket',
          type: 'is-danger',
          onConfirm: () => {
            this.$store.dispatch('deleteRecord', [ 'order', this.record ]).then(() => {
              this.$router.push('/support-tickets')
            })
          }
        })
      },
      deleteUpload (record, url) {
        this.$buefy.dialog.confirm({
          title: 'Deleting Upload',
          message: 'Are you sure you want to <b>delete</b> this upload?',
          confirmText: 'Delete Upload',
          type: 'is-danger',
          onConfirm: () => {
            const index = record.data.uploads.indexOf(url)
            record.data.uploads.splice(index, 1)
            this.$store.dispatch('saveRecord', [ 'order', record ])
          }
        })
      }
    }
  }
</script>
